import React, { useEffect, useState, useMemo } from 'react';
import { Container, Row, Col, Card, Button, Table, Alert, Spinner, Form, InputGroup } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { getSongById } from '../services/api';
import {SongRequest} from "../types/SongRequest";

const EventDetails: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const [requests, setRequests] = useState<SongRequest[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const fetchRequests = async () => {
    try {
      setError('');
      if (!eventId) {
        setError('Event ID is missing');
        return;
      }
      const response = await getSongById(eventId);
      setRequests(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load song requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
    const interval = setInterval(() => {
      fetchRequests();
    }, 5000);
    return () => clearInterval(interval);
  }, [eventId]);

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const filteredSortedRequests = useMemo(() => {
    const filtered = requests.filter((req) => {
      const query = searchQuery.toLowerCase();
      return (
          req.song_name.toLowerCase().includes(query) ||
          req.artist_name.toLowerCase().includes(query) ||
          req.requester_name.toLowerCase().includes(query) ||
          req.message.toLowerCase().includes(query)
      );
    });
    filtered.sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    });
    return filtered;
  }, [requests, searchQuery, sortOrder]);

  const renderSongName = (name: string) => {
    const urlPattern = /^(http|https):\/\//i;
    if (urlPattern.test(name)) {
      return (
          <a href={name} target="_blank" rel="noopener noreferrer">
            {name}
          </a>
      );
    }
    return name;
  };

  return (
      <Container className="py-4">
        <Row className="mb-3">
          <Col>
            <h1>Event Details</h1>
            <p>Viewing song requests for event: {eventId}</p>
          </Col>
        </Row>

        {error && (
            <Row className="mb-3">
              <Col>
                <Alert variant="danger" onClose={() => setError('')} dismissible>
                  {error}
                </Alert>
              </Col>
            </Row>
        )}

        <Row className="mb-3">
          <Col md={6}>
            <InputGroup>
              <Form.Control
                  type="text"
                  placeholder="Search requests..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
              />
            </InputGroup>
          </Col>
          <Col md={6} className="text-md-end mt-2 mt-md-0">
            <Button variant="outline-secondary" onClick={toggleSortOrder}>
              Sort by {sortOrder === 'asc' ? 'Oldest' : 'Newest'}
            </Button>
          </Col>
        </Row>

        {loading ? (
            <Row className="justify-content-center">
              <Spinner animation="border" role="status" className="my-4" />
            </Row>
        ) : (
            <Row>
              <Col>
                <Card className="shadow-sm">
                  <Card.Body className="p-0">
                    {filteredSortedRequests.length > 0 ? (
                        <div className="table-responsive">
                          <Table bordered hover className="mb-0 align-middle">
                            <thead className="table-light">
                            <tr>
                              <th style={{ width: '40px', whiteSpace: 'nowrap' }}>#</th>
                              <th style={{ width: '25%' }}>Song Name</th>
                              <th style={{ width: '20%' }}>Requester</th>
                              <th style={{ width: '35%' }}>Message</th>
                              <th style={{ width: '10%', whiteSpace: 'nowrap' }}>Donation</th>
                              <th style={{ width: '15%', whiteSpace: 'nowrap' }}>Created At</th>
                            </tr>
                            </thead>
                            <tbody>
                            {filteredSortedRequests.map((req, index) => (
                                <tr key={req.id}>
                                  <td>{index + 1}</td>
                                  <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                    {renderSongName(req.song_name)}
                                  </td>
                                  <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                    {req.requester_name || '-'}
                                  </td>
                                  <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                    {req.message || '-'}
                                  </td>
                                  <td>{req.donation_amount !== null ? `$${req.donation_amount.toFixed(2)}` : '-'}</td>
                                  <td>{new Date(req.created_at).toLocaleString()}</td>
                                </tr>
                            ))}
                            </tbody>
                          </Table>
                        </div>

                    ) : (
                        <p className="p-3">No song requests found.</p>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

        )}
      </Container>
  );
};

export default EventDetails;
